import * as Service from 'types/service';
import { Button, IconButton } from 'bento';
import { EventAttributesActionLocation } from 'utils/analytics/amplitudeEventConstants';
import { IconBook, IconBubbleMessage, IconCrossCircle } from '@tock/icons';
import { ViewMode } from 'routes/authenticated/app/bookings/CalendarAndServiceRoute';
import { css } from '@emotion/react';
import { openWalkinSidePanel } from 'utils/service/walkInUtils';
import { serviceConfigSelector } from 'selectors/service/serviceConfigSelector';
import { useActions } from '@tock/shared/utils/connect';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import ModalNames from 'constants/service/modalNames';
import React from 'react';

interface Props {
  party: Service.Party;
  isReadOnly: boolean;
  onCancelPurchase(party?: Service.Party): void;
  onClose(): void;
  onEditReservation(actionLocation?: EventAttributesActionLocation): void;
  handleSelectParty(party: Service.Party): void;
  handleRemoveWalkIn(): void;
  viewMode: ViewMode;
}

const PartyPopoverFooter: React.FC<React.PropsWithChildren<Props>> = ({
  party,
  isReadOnly,
  onCancelPurchase,
  onClose,
  onEditReservation,
  handleSelectParty,
  handleRemoveWalkIn,
  viewMode,
}) => {
  const { actions } = useActions();
  const serviceConfig = useSelector(serviceConfigSelector);
  const { textingEnabled, textingAvailable } = serviceConfig;
  const partyIsExpected =
    party.state === 'EXPECTED' ||
    party.state === 'ARRIVED' ||
    party.state === 'PARTIALLY_ARRIVED' ||
    party.state === 'PARTIALLY_SEATED';
  const isFlexExpectedWalkInsEnabled = useFeatureFlag('FLEX_EXPECTED_WALK_INS');

  const txtMessageButton = () => {
    const handleShowTextMessageModal = () => {
      if (!party) {
        return;
      }
      handleSelectParty(party);
      actions.reservationsSidePanel.closeSidePanel();
      actions.serviceUiModal.openGlobalModal(ModalNames.SendTextMessageModal);
      onClose();
    };

    return partyIsExpected ? (
      textingAvailable ? (
        <IconButton
          aria-label="Text message party"
          variant="outlined"
          disabled={!textingEnabled}
          onClick={handleShowTextMessageModal}
          data-testid="SMS-icon-button"
          size="large"
        >
          <IconBubbleMessage />
        </IconButton>
      ) : null
    ) : textingAvailable ? (
      <Button
        aria-label="Text message party"
        startIcon={<IconBubbleMessage />}
        disabled={!textingEnabled}
        onClick={handleShowTextMessageModal}
        size="large"
        data-testid="SMS-text-button"
      >
        Text
      </Button>
    ) : null;
  };

  const editReservationButton = () => {
    let disabled = false;
    let onClick: () => void = () => {
      return;
    };

    const handleEditWalkIn = (): void => {
      const { serviceUiWalkIn } = actions;
      const newFlowExecutionId = uuidv4();
      actions.flowTrack.setFlowExecutionId(newFlowExecutionId);

      handleSelectParty(party);
      // once all the panels are in, this will go away and you just open the panel you want.
      const hasQuotedTimeMinutes = isFlexExpectedWalkInsEnabled
        ? party.quotedTimeMinutes !== undefined
        : !!party.quotedTimeMinutes;
      if (hasQuotedTimeMinutes) {
        serviceUiWalkIn.openWalkInPanelForEditing({ walkInType: 'QUOTED', walkInParty: party });
        openWalkinSidePanel(actions, newFlowExecutionId, 'EDIT', viewMode.mode, 'WalkIn panel');
      } else {
        serviceUiWalkIn.openWalkInPanelForEditing({ walkInType: 'EXPECTED', walkInParty: party });
        openWalkinSidePanel(actions, newFlowExecutionId, 'EDIT', viewMode.mode, 'WalkIn panel');
      }
      onClose();
    };

    const handleEditReservation = () => {
      if (party.purchase?.cancelledOrRefunded) {
        return;
      }
      handleSelectParty(party);
      actions.reservationsSidePanel.closeSidePanel();
      onClose();
      setTimeout(() => onEditReservation(EventAttributesActionLocation.PARTY_POPOVER));
    };

    if (party.type === 'Booking') {
      disabled = party.purchase?.cancelledOrRefunded ?? isReadOnly;
      onClick = handleEditReservation;
    } else if (party.type === 'WalkIn') {
      disabled = isReadOnly;
      onClick = handleEditWalkIn;
    }

    return (
      <Button
        aria-label="Edit Reservation"
        variant="contained"
        color="primary"
        disabled={disabled}
        startIcon={<IconBook />}
        onClick={onClick}
        size="large"
        css={css`
          flex: 1 1 auto;
        `}
      >
        Edit
      </Button>
    );
  };

  const handleCancelReservation = () => {
    party.type === 'WalkIn' ? handleRemoveWalkIn() : onCancelPurchase(party);
    onClose();
  };

  return (
    <div
      data-testid="party-popover-footer"
      css={css`
        display: flex;
        gap: 1rem;
      `}
    >
      {editReservationButton()}
      {(textingAvailable || partyIsExpected) && (
        <div
          css={css`
            display: flex;
            flex: ${textingAvailable && partyIsExpected ? '1 1 auto' : '0'};
            justify-content: space-between;
          `}
        >
          {txtMessageButton()}
          {partyIsExpected && (
            <IconButton
              aria-label="Cancel Reservation"
              data-testid="cancel-reservation"
              variant="outlined"
              color="danger"
              onClick={handleCancelReservation}
              size="large"
            >
              <IconCrossCircle />
            </IconButton>
          )}
        </div>
      )}
    </div>
  );
};

export default PartyPopoverFooter;
